import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpResponse, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, from, throwError } from 'rxjs';
import { Platform } from '@ionic/angular';
import { Storage } from '@ionic/storage-angular';
import { environment } from '../environments/environment';
import { UserService } from 'src/app/services/user.service';
import { HTTP } from '@awesome-cordova-plugins/http/ngx';

type HttpMethod = 'get' | 'post' | 'put' | 'patch' | 'head' | 'delete' | 'upload' | 'download';

@Injectable()
export class NativeHttpInterceptor implements HttpInterceptor {
  constructor(
    private nativeHttp: HTTP,
    private platform: Platform,
    private storage: Storage,
    private userService: UserService
  ) { }

  public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {    
    if (!this.platform.is('cordova')) {
      let token = this.userService.getAuthToken();
      let headers = {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }

      if(token) {
        headers["x-access-token"] = token;        
      }      
      
      let authenticatedRequest = request.clone({setHeaders: headers})
      return next.handle(authenticatedRequest);
    } else {
      return from(this.handleNativeRequest(request));
    }
  }

  private parseNativeErrorBody(raw: unknown): unknown {
    if (raw == null) {
      return { message: 'Request failed' };
    }
    if (typeof raw !== 'string') {
      return raw;
    }
    const trimmed = raw.trim();
    if (!trimmed) {
      return { message: 'Request failed' };
    }
    try {
      return JSON.parse(trimmed);
    } catch {
      if (trimmed.startsWith('<!DOCTYPE') || trimmed.startsWith('<html') || trimmed.startsWith('<')) {
        return { message: 'Service unavailable' };
      }
      return { message: trimmed.slice(0, 500) };
    }
  }

  private rejectNativeHttpError(error: Record<string, unknown>): Promise<never> {
    return Promise.reject(new HttpErrorResponse({
      error: this.parseNativeErrorBody(error['error']),
      status: typeof error['status'] === 'number' ? error['status'] : 0,
      statusText: typeof error['statusText'] === 'string' ? error['statusText'] : 'Error',
      url: typeof error['url'] === 'string' ? error['url'] : undefined,
    }));
  }

  private async handleNativeRequest(request: HttpRequest<any>): Promise<HttpResponse<any>> {
    let token = this.userService.getAuthToken();
    const headerKeys = request.headers.keys();
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    };

    if(token) {
      headers["x-access-token"] = token;
    }

    headerKeys.forEach((key) => {
      headers[key] = request.headers.get(key);
    });

    try {
      await this.platform.ready();

      const method = <HttpMethod> request.method.toLowerCase();
      const nativeHttpResponse = await this.nativeHttp.sendRequest(request.url, {
        method: method,
        data: request.body,
        headers: headers,
        serializer: 'json',
      });

      let body;
      try {
        body = JSON.parse(nativeHttpResponse.data);
      } catch (error) {
        body = { response: nativeHttpResponse.data };
      }

      const response = new HttpResponse({
        body: body,
        status: nativeHttpResponse.status,
        headers: new HttpHeaders(nativeHttpResponse.headers),
        url: nativeHttpResponse.url,
      });

      // console.log(`returning from http request, body: ${JSON.stringify(body)}`)
      return Promise.resolve(response);
    } catch (error) {
      if (!error?.status) { return Promise.reject(error); }
      return this.rejectNativeHttpError(error as Record<string, unknown>);
    }
  }

  handleError(error: HttpErrorResponse) {
    return throwError(error);
  }
}
